import React, { useCallback, useEffect, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { getProducts } from '../../clients/product_client';
import { formatProtocolProductLabel } from '../../utils/protocols';
import {
  abortListSearchController,
  isRunnableListSearchQuery,
  MIN_LIST_SEARCH_CHARS,
} from '../../utils/listSearchRequest';
import { logError, logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'ProductSearchMultiselect';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Multi-select product picker backed by server-side search.
 *
 * Options come from `GET products/?search=…`, which matches catalogue number
 * and name. With `labInventoryOnly` the request pins
 * `supplier_catalogue_filter=false`, so global supplier catalog (Shop) rows can
 * never be offered — those are rejected by the protocol endpoints.
 *
 * @param {Object} props
 * @param {string} props.token - Access token for the current user.
 * @param {Array<{ value: number|string, label: string }>} props.selectedProducts
 * @param {Function} props.setSelectedProducts - Receives the new option array.
 * @param {string} [props.label]
 * @param {string} [props.helperText]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.labInventoryOnly=true] - Exclude Shop catalog rows.
 */
const ProductSearchMultiselect = ({
  token,
  selectedProducts,
  setSelectedProducts,
  label = 'Linked products',
  helperText = '',
  disabled = false,
  labInventoryOnly = true,
}) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isMountedRef = useRef(true);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      abortListSearchController(currentRequestRef);
    };
  }, []);

  const fetchProducts = useCallback(
    searchValue => {
      abortListSearchController(currentRequestRef);

      const controller = new AbortController();
      currentRequestRef.current = controller;
      setIsLoading(true);

      // getProducts drives a state setter; this picker only needs `results`.
      getProducts(token, () => {}, {
        searchInput: searchValue,
        supplierCatalogueFilter: labInventoryOnly ? 'false' : '',
        signal: controller.signal,
      })
        .then(response => {
          if (currentRequestRef.current !== controller) return;
          currentRequestRef.current = null;

          if (response?.canceled) {
            setIsLoading(false);
            return;
          }
          if (!isMountedRef.current) return;

          if (response?.success) {
            const results = Array.isArray(response.results)
              ? response.results
              : [];
            setOptions(
              results.map(product => ({
                value: product.id,
                label: formatProtocolProductLabel(product),
              }))
            );
            logInfo(LOG_CONTEXT, 'product search results', {
              searchValue,
              count: results.length,
              labInventoryOnly,
            });
          } else {
            setOptions([]);
            logError(LOG_CONTEXT, 'product search failed', {
              searchValue,
              response,
            });
          }
          setIsLoading(false);
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) return;
          currentRequestRef.current = null;
          if (!isMountedRef.current) return;
          logError(LOG_CONTEXT, 'product search threw', error);
          setOptions([]);
          setIsLoading(false);
        });
    },
    [token, labInventoryOnly]
  );

  const handleInputChange = (event, value, reason) => {
    // Selecting an option resets the input; that must not trigger a new search.
    if (reason === 'reset') return;

    setInputValue(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (!isRunnableListSearchQuery(value)) {
      setIsLoading(false);
      return;
    }
    if (!value) {
      setOptions([]);
      setIsLoading(false);
      abortListSearchController(currentRequestRef);
      return;
    }

    debounceTimeoutRef.current = setTimeout(() => {
      fetchProducts(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const resolvedHelperText =
    helperText ||
    `Search by catalogue number or name (${MIN_LIST_SEARCH_CHARS}+ characters)`;

  return (
    <Autocomplete
      multiple
      id="product_search_multiselect"
      options={options}
      value={selectedProducts}
      inputValue={inputValue}
      disabled={disabled}
      loading={isLoading}
      // Filtering happens on the server; keep MUI from re-filtering the page.
      filterOptions={x => x}
      onInputChange={handleInputChange}
      onChange={(event, newValue) => setSelectedProducts(newValue)}
      getOptionLabel={option => option?.label ?? ''}
      isOptionEqualToValue={(option, value) =>
        Boolean(option && value) && String(option.value) === String(value.value)
      }
      noOptionsText={
        isRunnableListSearchQuery(inputValue) && inputValue
          ? 'No matching lab products'
          : 'Type to search products'
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.value}>
          {option.label}
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          helperText={resolvedHelperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={18} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ProductSearchMultiselect;
